// stocai_frontend/src/components/HomePage/components/Action/AddNewObjective.jsx

import React, { useRef, useEffect, useState } from "react";
import concentricArrow from "../../../../assets/concentric_arrow.png";
import meter from "../../../../assets/meter.png";
import arrow from "../../../../assets/arrow.png";

const AddNewObjective = ({ isOpen, onClose, onSave, goalTitle = "Visibility" }) => {
  const [objectiveText, setObjectiveText] = useState("");
  const [shortTermInitiatives, setShortTermInitiatives] = useState([
    { id: 1, text: "" }
  ]);
  const [mediumTermInitiatives, setMediumTermInitiatives] = useState([
    { id: 1, text: "" }
  ]);
  const [lineHeight, setLineHeight] = useState(200);
  const [isSaving, setIsSaving] = useState(false); // ✅ ADD THIS
  
  const lineStartRef = useRef(null);
  const lastArrowRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setObjectiveText("");
      setShortTermInitiatives([{ id: 1, text: "" }]);
      setMediumTermInitiatives([{ id: 1, text: "" }]);
      setIsSaving(false); // ✅ RESET SAVING STATE
    }
  }, [isOpen]);

  // Recalculate line height whenever initiatives change
  useEffect(() => {
    if (isOpen && lineStartRef.current && lastArrowRef.current) {
      setTimeout(() => {
        const startRect = lineStartRef.current.getBoundingClientRect();
        const arrowRect = lastArrowRef.current.getBoundingClientRect();
        const calculatedHeight = arrowRect.top - startRect.top + 4;
        setLineHeight(calculatedHeight);
      }, 10);
    }
  }, [shortTermInitiatives, mediumTermInitiatives, isOpen]);

  if (!isOpen) return null;

  const handleAddShortTerm = () => {
    const newId = Math.max(...shortTermInitiatives.map(i => i.id), 0) + 1;
    setShortTermInitiatives(prev => [...prev, { id: newId, text: "" }]);
  };

  const handleAddMediumTerm = () => {
    const newId = Math.max(...mediumTermInitiatives.map(i => i.id), 0) + 1;
    setMediumTermInitiatives(prev => [...prev, { id: newId, text: "" }]);
  };

  const handleSaveObjective = async () => { // ✅ MAKE ASYNC
    // ✅ PREVENT DOUBLE SUBMISSION
    if (isSaving) {
      return;
    }

    // Validate
    if (!objectiveText.trim()) {
      alert("Please enter an objective title");
      return;
    }

    // Filter out empty initiatives and format for backend
    const shortTerm = shortTermInitiatives
      .filter(init => init.text.trim())
      .map(init => ({
        title: init.text,
        horizon: "short_term",
        xp_value: 5
      }));

    const mediumTerm = mediumTermInitiatives
      .filter(init => init.text.trim())
      .map(init => ({
        title: init.text,
        horizon: "medium_term",
        xp_value: 10
      }));

    const allInitiatives = [...shortTerm, ...mediumTerm];

    if (allInitiatives.length === 0) {
      alert("Please add at least one initiative");
      return;
    }

    // ✅ SET SAVING STATE
    try {
      setIsSaving(true);
      
      await onSave({
        title: objectiveText,
        initiatives: allInitiatives
      });
      
      // Note: Don't set isSaving to false here because the modal will close
      // and useEffect will reset it when modal reopens
      
    } catch (error) {
      console.error("Error saving objective:", error);
      setIsSaving(false); // ✅ RESET ON ERROR
      alert("Failed to save objective. Please try again.");
    }
  };

  const isLastShortTerm = (index) => {
    return index === shortTermInitiatives.length - 1 && mediumTermInitiatives.length === 0;
  };

  const isLastMediumTerm = (index) => {
    return index === mediumTermInitiatives.length - 1;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000CC]">
      <div className="w-[1189px] h-[788px] bg-white rounded-[20px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="w-full h-[92.57px] bg-[#259594] flex items-center px-8">
          <h2 className="text-[28px] leading-[1.35] font-medium text-white capitalize">
            Add Objective
          </h2>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-9 py-6">
          {/* Objective Title */}
          <h3 className="text-[24px] leading-[1.35] font-medium text-[#424242] capitalize mb-4">
            {goalTitle} Objective
          </h3>

          {/* Objective Input Box */}
          <div className="w-[1085px] h-[84px] border border-[#DBDBDB] rounded-lg flex items-center px-5 mb-6 relative">
            <img
              src={concentricArrow}
              alt=""
              className="w-[42px] h-[42px] object-contain mr-4"
            />
            
            <input
              type="text"
              value={objectiveText}
              onChange={(e) => setObjectiveText(e.target.value)}
              placeholder="Type the objective..."
              className="flex-1 text-[21px] leading-[1.35] font-normal text-black placeholder:text-[#CCCCCC] outline-none"
              disabled={isSaving} // ✅ DISABLE INPUT WHILE SAVING
            />
          </div>

          {/* Main Content with Vertical Line */}
          <div className="relative">
            {/* Reference point for line start */}
            <div ref={lineStartRef} className="absolute left-[41px] -top-6 w-0 h-0" />
            
            {/* Vertical Line */}
            <div 
              className="absolute left-[41px] -top-6 border-l border-[#D1D1D1]"
              style={{ height: `${lineHeight}px` }}
            />

            {/* Short Term Initiatives */}
            <div className="mb-8">
              <div className="flex items-center mb-4 ml-20">
                <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF]">
                  Short term initiatives
                </p>
                <div className="ml-4 flex-1 border-b border-[#E4E4E4]" />
              </div>

              <div className="space-y-4">
                {shortTermInitiatives.map((item, index) => (
                  <div key={item.id} className="relative">
                    <img
                      ref={isLastShortTerm(index) ? lastArrowRef : null}
                      src={arrow}
                      alt=""
                      className="absolute left-[41px] top-[15px] w-[17.6px] h-auto object-contain"
                    />
                    
                    <div className="flex items-center gap-4 pl-[84px]">
                      <img
                        src={meter}
                        alt=""
                        className="w-[31px] h-[30px] object-contain flex-shrink-0"
                      />
                      
                      <input
                        type="text"
                        value={item.text}
                        onChange={(e) => {
                          setShortTermInitiatives(prev =>
                            prev.map(i => i.id === item.id ? { ...i, text: e.target.value } : i)
                          );
                        }}
                        placeholder="Initiative 1"
                        className="flex-1 text-[15px] leading-[1] font-normal text-black placeholder:text-[#CCCCCC] outline-none"
                        disabled={isSaving} // ✅ DISABLE INPUT WHILE SAVING
                      />

                      <span 
                        className={`text-[12px] leading-[1.1] font-medium uppercase text-right ${
                          item.text.trim() ? 'text-[#54B0AF]' : 'text-[#8C8C8C]'
                        }`}
                      >
                        5XP
                      </span>
                    </div>

                    {/* Separator line */}
                    <div className="ml-[84px] mt-3 w-[1003px] border-t border-[#E4E4E4]" />
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddShortTerm}
                disabled={isSaving} // ✅ DISABLE BUTTON WHILE SAVING
                className="mt-4 ml-[84px] text-[15px] leading-[1.45] font-medium text-[#666666] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Add New Initiative
              </button>
            </div>

            {/* Medium Term Initiatives */}
            <div className="mb-8">
              <div className="flex items-center mb-4 ml-20">
                <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF]">
                  Medium term initiatives
                </p>
                <div className="ml-4 flex-1 border-b border-[#E4E4E4]" />
              </div>

              <div className="space-y-4">
                {mediumTermInitiatives.map((item, index) => (
                  <div key={item.id} className="relative">
                    <img
                      ref={isLastMediumTerm(index) ? lastArrowRef : null}
                      src={arrow}
                      alt=""
                      className="absolute left-[41px] top-[15px] w-[17.6px] h-auto object-contain"
                    />
                    
                    <div className="flex items-center gap-4 pl-[84px]">
                      <img
                        src={meter}
                        alt=""
                        className="w-[31px] h-[30px] object-contain flex-shrink-0"
                      />
                      
                      <input
                        type="text"
                        value={item.text}
                        onChange={(e) => {
                          setMediumTermInitiatives(prev =>
                            prev.map(i => i.id === item.id ? { ...i, text: e.target.value } : i)
                          );
                        }}
                        placeholder="Initiative 1"
                        className="flex-1 text-[15px] leading-[1] font-normal text-black placeholder:text-[#CCCCCC] outline-none"
                        disabled={isSaving} // ✅ DISABLE INPUT WHILE SAVING
                      />

                      <span 
                        className={`text-[12px] leading-[1.1] font-medium uppercase text-right ${
                          item.text.trim() ? 'text-[#54B0AF]' : 'text-[#8C8C8C]'
                        }`}
                      >
                        10XP
                      </span>
                    </div>

                    {/* Separator line */}
                    <div className="ml-[84px] mt-3 w-[1003px] border-t border-[#E4E4E4]" />
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddMediumTerm}
                disabled={isSaving} // ✅ DISABLE BUTTON WHILE SAVING
                className="mt-4 ml-[84px] text-[15px] leading-[1.45] font-medium text-[#666666] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Add New Initiative
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full h-[93.5px] bg-[#EAF6F6] flex items-center justify-between px-8">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving} // ✅ DISABLE CANCEL WHILE SAVING
            className="text-[20px] leading-[1.35] font-normal text-[#424242] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSaveObjective}
            disabled={isSaving} // ✅ DISABLE SAVE BUTTON WHILE SAVING
            className="w-[178px] h-[46px] rounded-[6.2px] bg-[#54B0AF] flex items-center justify-center px-[25px] py-[13px] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            <span className="text-[17.01px] leading-[1] font-medium text-white">
              {isSaving ? "Adding..." : "Save Objective"} {/* ✅ SHOW LOADING TEXT */}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewObjective;


// // stocai_frontend/src/components/HomePage/components/Action/AddNewObjective.jsx

// import React, { useRef, useEffect, useState } from "react";
// import concentricArrow from "../../../../assets/concentric_arrow.png";
// import meter from "../../../../assets/meter.png";
// import arrow from "../../../../assets/arrow.png";

// const AddNewObjective = ({ isOpen, onClose, onSave, goalTitle = "Visibility" }) => {
//   const [objectiveText, setObjectiveText] = useState("");
//   const [shortTermInitiatives, setShortTermInitiatives] = useState([
//     { id: 1, text: "" }
//   ]);
//   const [mediumTermInitiatives, setMediumTermInitiatives] = useState([
//     { id: 1, text: "" }
//   ]);
//   const [lineHeight, setLineHeight] = useState(200);
  
//   const lineStartRef = useRef(null);
//   const lastArrowRef = useRef(null);

//   // Reset form when modal opens
//   useEffect(() => {
//     if (isOpen) {
//       setObjectiveText("");
//       setShortTermInitiatives([{ id: 1, text: "" }]);
//       setMediumTermInitiatives([{ id: 1, text: "" }]);
//     }
//   }, [isOpen]);

//   // Recalculate line height whenever initiatives change
//   useEffect(() => {
//     if (isOpen && lineStartRef.current && lastArrowRef.current) {
//       setTimeout(() => {
//         const startRect = lineStartRef.current.getBoundingClientRect();
//         const arrowRect = lastArrowRef.current.getBoundingClientRect();
//         const calculatedHeight = arrowRect.top - startRect.top + 4;
//         setLineHeight(calculatedHeight);
//       }, 10);
//     }
//   }, [shortTermInitiatives, mediumTermInitiatives, isOpen]);

//   if (!isOpen) return null;

//   const handleAddShortTerm = () => {
//     const newId = Math.max(...shortTermInitiatives.map(i => i.id), 0) + 1;
//     setShortTermInitiatives(prev => [...prev, { id: newId, text: "" }]);
//   };

//   const handleAddMediumTerm = () => {
//     const newId = Math.max(...mediumTermInitiatives.map(i => i.id), 0) + 1;
//     setMediumTermInitiatives(prev => [...prev, { id: newId, text: "" }]);
//   };

//   const handleSaveObjective = () => {
//     // Validate
//     if (!objectiveText.trim()) {
//       alert("Please enter an objective title");
//       return;
//     }

//     // Filter out empty initiatives and format for backend
//     const shortTerm = shortTermInitiatives
//       .filter(init => init.text.trim())
//       .map(init => ({
//         title: init.text,
//         horizon: "short_term",
//         xp_value: 5
//       }));

//     const mediumTerm = mediumTermInitiatives
//       .filter(init => init.text.trim())
//       .map(init => ({
//         title: init.text,
//         horizon: "medium_term",
//         xp_value: 10
//       }));

//     const allInitiatives = [...shortTerm, ...mediumTerm];

//     if (allInitiatives.length === 0) {
//       alert("Please add at least one initiative");
//       return;
//     }

//     onSave({
//       title: objectiveText,
//       initiatives: allInitiatives
//     });
//   };

//   const isLastShortTerm = (index) => {
//     return index === shortTermInitiatives.length - 1 && mediumTermInitiatives.length === 0;
//   };

//   const isLastMediumTerm = (index) => {
//     return index === mediumTermInitiatives.length - 1;
//   };

//   return (
//     <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000CC]">
//       <div className="w-[1189px] h-[788px] bg-white rounded-[20px] overflow-hidden flex flex-col">
//         {/* Header */}
//         <div className="w-full h-[92.57px] bg-[#259594] flex items-center px-8">
//           <h2 className="text-[28px] leading-[1.35] font-medium text-white capitalize">
//             Add Objective
//           </h2>
//         </div>

//         {/* Body */}
//         <div className="flex-1 overflow-y-auto px-9 py-6">
//           {/* Objective Title */}
//           <h3 className="text-[24px] leading-[1.35] font-medium text-[#424242] capitalize mb-4">
//             {goalTitle} Objective
//           </h3>

//           {/* Objective Input Box */}
//           <div className="w-[1085px] h-[84px] border border-[#DBDBDB] rounded-lg flex items-center px-5 mb-6 relative">
//             <img
//               src={concentricArrow}
//               alt=""
//               className="w-[42px] h-[42px] object-contain mr-4"
//             />
            
//             <input
//               type="text"
//               value={objectiveText}
//               onChange={(e) => setObjectiveText(e.target.value)}
//               placeholder="Type the objective..."
//               className="flex-1 text-[21px] leading-[1.35] font-normal text-black placeholder:text-[#CCCCCC] outline-none"
//             />
//           </div>

//           {/* Main Content with Vertical Line */}
//           <div className="relative">
//             {/* Reference point for line start */}
//             <div ref={lineStartRef} className="absolute left-[41px] -top-6 w-0 h-0" />
            
//             {/* Vertical Line */}
//             <div 
//               className="absolute left-[41px] -top-6 border-l border-[#D1D1D1]"
//               style={{ height: `${lineHeight}px` }}
//             />

//             {/* Short Term Initiatives */}
//             <div className="mb-8">
//               <div className="flex items-center mb-4 ml-20">
//                 <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF]">
//                   Short term initiatives
//                 </p>
//                 <div className="ml-4 flex-1 border-b border-[#E4E4E4]" />
//               </div>

//               <div className="space-y-4">
//                 {shortTermInitiatives.map((item, index) => (
//                   <div key={item.id} className="relative">
//                     <img
//                       ref={isLastShortTerm(index) ? lastArrowRef : null}
//                       src={arrow}
//                       alt=""
//                       className="absolute left-[41px] top-[15px] w-[17.6px] h-auto object-contain"
//                     />
                    
//                     <div className="flex items-center gap-4 pl-[84px]">
//                       <img
//                         src={meter}
//                         alt=""
//                         className="w-[31px] h-[30px] object-contain flex-shrink-0"
//                       />
                      
//                       <input
//                         type="text"
//                         value={item.text}
//                         onChange={(e) => {
//                           setShortTermInitiatives(prev =>
//                             prev.map(i => i.id === item.id ? { ...i, text: e.target.value } : i)
//                           );
//                         }}
//                         placeholder="Initiative 1"
//                         className="flex-1 text-[15px] leading-[1] font-normal text-black placeholder:text-[#CCCCCC] outline-none"
//                       />

//                       <span 
//                         className={`text-[12px] leading-[1.1] font-medium uppercase text-right ${
//                           item.text.trim() ? 'text-[#54B0AF]' : 'text-[#8C8C8C]'
//                         }`}
//                       >
//                         5XP
//                       </span>
//                     </div>

//                     {/* Separator line */}
//                     <div className="ml-[84px] mt-3 w-[1003px] border-t border-[#E4E4E4]" />
//                   </div>
//                 ))}
//               </div>

//               <button
//                 type="button"
//                 onClick={handleAddShortTerm}
//                 className="mt-4 ml-[84px] text-[15px] leading-[1.45] font-medium text-[#666666]"
//               >
//                 + Add New Initiative
//               </button>
//             </div>

//             {/* Medium Term Initiatives */}
//             <div className="mb-8">
//               <div className="flex items-center mb-4 ml-20">
//                 <p className="text-[11px] leading-[1.45] font-bold uppercase text-[#54B0AF]">
//                   Medium term initiatives
//                 </p>
//                 <div className="ml-4 flex-1 border-b border-[#E4E4E4]" />
//               </div>

//               <div className="space-y-4">
//                 {mediumTermInitiatives.map((item, index) => (
//                   <div key={item.id} className="relative">
//                     <img
//                       ref={isLastMediumTerm(index) ? lastArrowRef : null}
//                       src={arrow}
//                       alt=""
//                       className="absolute left-[41px] top-[15px] w-[17.6px] h-auto object-contain"
//                     />
                    
//                     <div className="flex items-center gap-4 pl-[84px]">
//                       <img
//                         src={meter}
//                         alt=""
//                         className="w-[31px] h-[30px] object-contain flex-shrink-0"
//                       />
                      
//                       <input
//                         type="text"
//                         value={item.text}
//                         onChange={(e) => {
//                           setMediumTermInitiatives(prev =>
//                             prev.map(i => i.id === item.id ? { ...i, text: e.target.value } : i)
//                           );
//                         }}
//                         placeholder="Initiative 1"
//                         className="flex-1 text-[15px] leading-[1] font-normal text-black placeholder:text-[#CCCCCC] outline-none"
//                       />

//                       <span 
//                         className={`text-[12px] leading-[1.1] font-medium uppercase text-right ${
//                           item.text.trim() ? 'text-[#54B0AF]' : 'text-[#8C8C8C]'
//                         }`}
//                       >
//                         10XP
//                       </span>
//                     </div>

//                     {/* Separator line */}
//                     <div className="ml-[84px] mt-3 w-[1003px] border-t border-[#E4E4E4]" />
//                   </div>
//                 ))}
//               </div>

//               <button
//                 type="button"
//                 onClick={handleAddMediumTerm}
//                 className="mt-4 ml-[84px] text-[15px] leading-[1.45] font-medium text-[#666666]"
//               >
//                 + Add New Initiative
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="w-full h-[93.5px] bg-[#EAF6F6] flex items-center justify-between px-8">
//           <button
//             type="button"
//             onClick={onClose}
//             className="text-[20px] leading-[1.35] font-normal text-[#424242]"
//           >
//             Cancel
//           </button>

//           <button
//             type="button"
//             onClick={handleSaveObjective}
//             className="w-[178px] h-[46px] rounded-[6.2px] bg-[#54B0AF] flex items-center justify-center px-[25px] py-[13px]"
//           >
//             <span className="text-[17.01px] leading-[1] font-medium text-white">
//               Save Objective
//             </span>
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddNewObjective;